import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type Theme = 'dark' | 'light';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private themeSubject = new BehaviorSubject<Theme>('dark');
  public theme$ = this.themeSubject.asObservable();

  constructor() {
    this.loadTheme();
  }

  private loadTheme(): void {
    // Dark is the default: there is no light login screen, so an unset
    // preference must never resolve to light. Only an explicit saved choice
    // switches to light.
    const stored = localStorage.getItem('theme') as Theme;
    this.setTheme(stored === 'light' ? 'light' : 'dark');
  }

  setTheme(theme: Theme): void {
    this.applyTheme(theme);
    localStorage.setItem('theme', theme);
  }

  /**
   * Apply a theme to the DOM without persisting it. Used by the login screen,
   * which is always dark regardless of the user's saved preference — so it must
   * not overwrite a logged-in user's light choice.
   */
  applyThemeTransient(theme: Theme): void {
    this.applyTheme(theme);
  }

  /** Re-apply the persisted theme (defaults to dark when unset). */
  restoreStoredTheme(): void {
    const stored = localStorage.getItem('theme') as Theme;
    this.applyTheme(stored === 'light' ? 'light' : 'dark');
  }

  private applyTheme(theme: Theme): void {
    document.documentElement.setAttribute('data-theme', theme);
    this.themeSubject.next(theme);
  }

  toggleTheme(): void {
    const current = this.themeSubject.value;
    this.setTheme(current === 'dark' ? 'light' : 'dark');
  }

  getCurrentTheme(): Theme {
    return this.themeSubject.value;
  }
}
